import { dayjs } from 'src/libs/dayjs';
import { SosLocationDatapoint } from 'src/api/brandTracking';

export type ChartRow = {
  key: number;
  [keyword: string]: number;
};

export type TimeRangeMonths = 12 | 24 | 48 | null;

/**
 * Collect the distinct keywords present in the datapoints, brand first
 * (the backend does not guarantee ordering).
 */
export const getKeywords = (
  datapoints: SosLocationDatapoint[],
  brandKeyword: string | null,
): string[] => {
  const keywords = new Set<string>();
  datapoints.forEach((datapoint) => {
    datapoint.keywords.forEach(({ keyword }) => keywords.add(keyword));
  });
  const sorted = Array.from(keywords).sort();
  if (brandKeyword && keywords.has(brandKeyword)) {
    return [brandKeyword, ...sorted.filter((k) => k !== brandKeyword)];
  }
  return sorted;
};

/**
 * One chart row per month with each keyword's share of that month's total
 * search volume, in percent. Missing keywords count as 0 volume.
 */
export const toShareSeries = (
  datapoints: SosLocationDatapoint[],
  keywords: string[],
): ChartRow[] =>
  [...datapoints]
    .sort((a, b) => a.utcEpoch - b.utcEpoch)
    .map((datapoint) => {
      const volumes: Record<string, number> = {};
      datapoint.keywords.forEach(({ keyword, searchVolume }) => {
        volumes[keyword] = searchVolume;
      });
      const total = keywords.reduce(
        (sum, keyword) => sum + (volumes[keyword] ?? 0),
        0,
      );
      const row: ChartRow = { key: datapoint.utcEpoch };
      keywords.forEach((keyword) => {
        row[keyword] = total > 0 ? ((volumes[keyword] ?? 0) / total) * 100 : 0;
      });
      return row;
    });

/** One chart row per month with each keyword's absolute search volume. */
export const toVolumeSeries = (
  datapoints: SosLocationDatapoint[],
  keywords: string[],
): ChartRow[] =>
  [...datapoints]
    .sort((a, b) => a.utcEpoch - b.utcEpoch)
    .map((datapoint) => {
      const volumes: Record<string, number> = {};
      datapoint.keywords.forEach(({ keyword, searchVolume }) => {
        volumes[keyword] = searchVolume;
      });
      const row: ChartRow = { key: datapoint.utcEpoch };
      keywords.forEach((keyword) => {
        row[keyword] = volumes[keyword] ?? 0;
      });
      return row;
    });

/** Keep only the trailing N months; null means the full history. */
export const filterByTimeRange = (
  rows: ChartRow[],
  months: TimeRangeMonths,
): ChartRow[] => {
  if (months === null || rows.length === 0) return rows;
  return rows.slice(-months);
};

/**
 * Freshness label for the header, e.g. "Jul 2026". Input is the config's
 * latestDataDate (YYYY-MM-DD).
 */
export const formatLatestDataMonth = (
  latestDataDate: string | null,
): string | null => {
  if (!latestDataDate) return null;
  const date = dayjs(latestDataDate);
  if (!date.isValid()) return null;
  return date.format('MMM YYYY');
};
